import CHUVLogo from '@assets/images/logo_chuv.svg';
import { Typography } from '@mui/joy';
import { styled } from '@mui/joy/styles';
import { useTranslation } from 'next-i18next';

const ContentWrapper = styled('div')(({ theme }) => ({
  padding: '20px',
  lineHeight: '1.6',
  fontSize: '16px',
  color: theme.vars.palette.text.primary,
  width: '100%',
  boxSizing: 'border-box',

  '& h1': {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: theme.vars.palette.text.primary,
  },

  '& h2': {
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '16px',
    marginTop: '24px',
    color: theme.vars.palette.text.primary,
  },

  '& p': {
    marginBottom: '16px',
    textAlign: 'justify',
  },

  '& ul': {
    marginBottom: '16px',
    paddingLeft: '20px',
  },

  '& li': {
    marginBottom: '8px',
  },

  '& .contact-info': {
    backgroundColor: theme.vars.palette.background.level1,
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '20px',
  },

  '& .checkbox-list': {
    '& li': {
      listStyle: 'none',
      paddingLeft: '0',
      marginBottom: '12px',

      '&::before': {
        content: '"✓ "',
        color: theme.vars.palette.success.solidBg,
        fontWeight: 'bold',
        marginRight: '8px',
      },
    },
  },
}));

interface ConditionsContentProps {
  showScrollIndicator?: boolean;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
}

export default function ConditionsContent({
  showScrollIndicator = false,
  onScroll,
  containerRef,
}: ConditionsContentProps): JSX.Element {
  const { t } = useTranslation('common');
  return (
    <ContentWrapper
      ref={containerRef}
      onScroll={onScroll}
      style={{
        overflowY: showScrollIndicator ? 'auto' : 'visible',
        flex: showScrollIndicator ? 1 : undefined,
        minHeight: showScrollIndicator ? 0 : undefined,
      }}
    >
      <div style={{ textAlign: 'left', marginBottom: '32px', padding: '0' }}>
        <CHUVLogo style={{ height: '60px', width: 'auto' }} />
      </div>

      <Typography component="h1" level="h1">
        {t('legal.conditions.title')}
      </Typography>

      <Typography component="p">
        <strong>{t('legal.conditions.participantInfo')}</strong>
      </Typography>

      <Typography component="p">{t('legal.conditions.intro')}</Typography>

      <Typography component="p">{t('legal.conditions.voluntary')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.whyStudy.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.whyStudy.p1')}</Typography>

      <Typography component="p">{t('legal.conditions.whyStudy.p2')}</Typography>

      <Typography component="p">{t('legal.conditions.whyStudy.p3')}</Typography>

      <Typography component="p">{t('legal.conditions.whyStudy.p4')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.participation.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.participation.p1')}</Typography>

      <Typography component="p">{t('legal.conditions.participation.p2')}</Typography>

      <Typography component="p">{t('legal.conditions.participation.p3')}</Typography>

      <Typography component="p">{t('legal.conditions.participation.p4')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.studyStructure.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.studyStructure.p1')}</Typography>

      <Typography component="p">{t('legal.conditions.studyStructure.p2')}</Typography>

      <Typography component="p">{t('legal.conditions.studyStructure.p3')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.benefitsRisks.title')}
      </Typography>

      <Typography component="h3" level="h3" sx={{ fontSize: '18px', fontWeight: 'bold', mt: 2, mb: 1 }}>
        {t('legal.conditions.benefitsRisks.benefits.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.benefitsRisks.benefits.content')}</Typography>

      <Typography component="h3" level="h3" sx={{ fontSize: '18px', fontWeight: 'bold', mt: 2, mb: 1 }}>
        {t('legal.conditions.benefitsRisks.risks.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.benefitsRisks.risks.content')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.regulation.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.regulation.p1')}</Typography>

      <Typography component="p">{t('legal.conditions.regulation.p2')}</Typography>

      <Typography component="p">{t('legal.conditions.regulation.p3')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.studyEnd.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.studyEnd.p1')}</Typography>

      <Typography component="p">{t('legal.conditions.studyEnd.p2')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.noParticipation.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.noParticipation.content')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.funding.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.funding.p1')}</Typography>

      <Typography component="p">{t('legal.conditions.funding.p2')}</Typography>

      <Typography component="p">{t('legal.conditions.funding.p3')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.results.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.results.content')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.dataProtection.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.dataProtection.p1')}</Typography>

      <Typography component="p">{t('legal.conditions.dataProtection.p2')}</Typography>

      <Typography component="p">{t('legal.conditions.dataProtection.p3')}</Typography>

      <Typography component="p">{t('legal.conditions.dataProtection.p4')}</Typography>

      <Typography component="p">{t('legal.conditions.dataProtection.p5')}</Typography>

      <Typography component="p">{t('legal.conditions.dataProtection.p6')}</Typography>

      <Typography component="p">{t('legal.conditions.dataProtection.p7')}</Typography>

      <Typography component="p">{t('legal.conditions.dataProtection.p8')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.insurance.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.insurance.content')}</Typography>

      <Typography component="h2" level="h2">
        {t('legal.conditions.consent.title')}
      </Typography>

      <Typography component="p">{t('legal.conditions.consent.p1')}</Typography>

      <Typography component="p">{t('legal.conditions.consent.p2')}</Typography>

      <div className="contact-info">
        <Typography component="p" sx={{ mb: 1, fontWeight: 'bold' }}>
          {t('legal.conditions.consent.studyTitle')}
        </Typography>
        <Typography component="p" sx={{ mb: 2 }}>
          {t('legal.conditions.consent.studyDescription')}
        </Typography>

        <Typography component="p" sx={{ mb: 1, fontWeight: 'bold' }}>
          {t('legal.conditions.consent.simplifiedTitle')}
        </Typography>
        <Typography component="p" sx={{ mb: 2 }}>
          {t('legal.conditions.consent.simplifiedDescription')}
        </Typography>

        <Typography component="p" sx={{ mb: 1, fontWeight: 'bold' }}>
          {t('legal.conditions.consent.basecNumber')}
        </Typography>
        <Typography component="p" sx={{ mb: 2 }}>
          {t('legal.conditions.consent.basecValue')}
        </Typography>

        <Typography component="p" sx={{ mb: 1, fontWeight: 'bold' }}>
          {t('legal.conditions.consent.responsibleInstitution')}
        </Typography>
        <Typography component="p" sx={{ mb: 2 }}>
          {t('legal.conditions.consent.institutionValue')
            .split('\n')
            .map((line, index, array) => (
              <span key={`institution-${crypto.randomUUID()}`}>
                {line}
                {index < array.length - 1 && <br />}
              </span>
            ))}
        </Typography>

        <Typography component="p" sx={{ mb: 1, fontWeight: 'bold' }}>
          {t('legal.conditions.consent.principalInvestigator')}
        </Typography>
        <Typography component="p" sx={{ mb: 1 }}>
          {t('legal.conditions.consent.investigatorName')}
        </Typography>

        <Typography component="p" sx={{ mb: 1, fontWeight: 'bold' }}>
          {t('legal.conditions.consent.contact')}
        </Typography>
        <Typography component="p">
          {t('legal.conditions.consent.contactInfo')
            .split('\n')
            .map((line, index, array) => (
              <span key={`contact-${crypto.randomUUID()}`}>
                {line}
                {index < array.length - 1 && <br />}
              </span>
            ))}
        </Typography>
      </div>

      <ul className="checkbox-list">
        <li>{t('legal.conditions.consent.checkboxes.informed')}</li>
        <li>{t('legal.conditions.consent.checkboxes.voluntary')}</li>
        <li>{t('legal.conditions.consent.checkboxes.timeToDecide')}</li>
        <li>{t('legal.conditions.consent.checkboxes.pdfCopy')}</li>
        <li>{t('legal.conditions.consent.checkboxes.withdraw')}</li>
        <li>{t('legal.conditions.consent.checkboxes.dataTransmission')}</li>
        <li>{t('legal.conditions.consent.checkboxes.insurance')}</li>
      </ul>

      <Typography component="p" sx={{ fontStyle: 'italic', mt: 2 }}>
        {t('legal.conditions.consent.electronicDate')}
      </Typography>
    </ContentWrapper>
  );
}
